import { useEffect, useState, SyntheticEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// material-ui
import {
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  LinearProgressProps,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project import
import useScriptRef from 'hooks/useScriptRef';
import IconButton from 'components/@extended/IconButton';
import AnimateButton from 'components/@extended/AnimateButton';
import { strengthColor, strengthIndicator, strengthSteps } from 'utils/password-strength';
import { StyledButton } from 'components/Buttons/StyledButton';
import Spinner from 'components/Spinner';

// types
import { StringColorProps } from 'types/password';

// assets
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

// redux
import { useChangePasswordMutation, useResetForgotPasswordMutation } from 'store/reducers/api/auth';

// hooks
import useSnackbar from 'hooks/useSnackbar';

// utils
import { initZxcvbn } from 'utils/zxcvbn-setup';

// ============================|| STATIC - RESET PASSWORD ||============================ //

const AuthChangePassword = ({ changePasswordFlow = false, onSuccess }: { changePasswordFlow?: boolean; onSuccess?: () => void }) => {
  const scriptedRef = useScriptRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [resetForgotPassword] = useResetForgotPasswordMutation();
  const [changePasswordMutation] = useChangePasswordMutation();

  const [level, setLevel] = useState<StringColorProps>();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: SyntheticEvent) => {
    event.preventDefault();
  };

  const changePassword = (value: string) => {
    if (value) {
      const temp = strengthIndicator(value);
      setLevel(strengthColor(temp));
    } else {
      setLevel(undefined);
    }
  };

  useEffect(() => {
    changePassword('');
  }, []);

  useEffect(() => {
    setLoading(true);
    initZxcvbn()
      .catch(() => snackbar.showError('Password strength checker failed to load.'))
      .finally(() => setLoading(false));
  }, [snackbar]);

  const renderPasswordStrengthIndicator = () => (
    <FormControl fullWidth sx={{ mt: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} container spacing={1}>
          {Object.keys(strengthSteps).map((key, index) => (
            <Grid item xs={4} key={index}>
              <LinearProgress
                color={strengthSteps[key as keyof typeof strengthSteps].color as LinearProgressProps['color']}
                variant="determinate"
                value={level && level.minScore >= strengthSteps[key as keyof typeof strengthSteps].minScore ? 100 : 0}
              />
            </Grid>
          ))}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" fontSize="0.75rem" textAlign={'center'}>
            {level?.label}
          </Typography>
        </Grid>
      </Grid>
    </FormControl>
  );

  if (loading) return <Spinner />;

  return (
    <>
      <Formik
        initialValues={{
          oldPassword: '',
          password: '',
          confirmPassword: '',
          submit: null
        }}
        validationSchema={Yup.object().shape({
          ...(changePasswordFlow && { oldPassword: Yup.string().max(255).required('Old password is required') }),
          password: Yup.string()
            .max(15, 'Password must not exceed 15 characters')
            .required('Password is required')
            .matches(
              /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()~]).{8,}$/,
              'The password should be at least 8 characters long, including at least one number, one special character, one capital letter, and one lowercase letter'
            )
            .matches(
              /^[A-Za-z\d!@#$%^&*()~]+$/,
              'Password must contain only English letters, numbers, and special characters. Spaces and non-English characters are not allowed'
            ),
          confirmPassword: Yup.string()
            .required('Confirm password is required')
            .when('password', {
              is: (val: string) => !!(val && val.length > 0),
              then: Yup.string().oneOf([Yup.ref('password')], 'Both passwords must match!')
            })
        })}
        onSubmit={async (values, { setErrors, setStatus }) => {
          try {
            const { password, oldPassword } = values;

            if (changePasswordFlow) {
              await changePasswordMutation({ newPassword: password, oldPassword }).unwrap();
            } else {
              if (!email || !token) {
                snackbar.showError('Invalid request');
                return;
              }

              await resetForgotPassword({ email, newPassword: password, token }).unwrap();
            }

            if (scriptedRef.current) {
              setStatus({ success: true });
              snackbar.showSuccess('Password changed successfully');

              onSuccess?.();

              if (!changePasswordFlow) {
                setTimeout(() => {
                  navigate('/login', { replace: true });
                }, 1000);
              }
            }
          } catch (err: any) {
            console.error(err);
            if (scriptedRef.current) {
              setStatus({ success: false });
              setErrors({ submit: err.data?.message ?? err.message });
            }
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values, isValid, dirty }) => {
          const disabledButton = !isValid || !dirty || isSubmitting;
          return (
            <form noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {changePasswordFlow && (
                  <Grid item xs={12}>
                    <Stack spacing={1}>
                      <InputLabel htmlFor="old-password-reset">Old Password</InputLabel>
                      <OutlinedInput
                        fullWidth
                        error={Boolean(touched.oldPassword && errors.oldPassword)}
                        id="old-password-reset"
                        type="password"
                        value={values.oldPassword}
                        name="oldPassword"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder="Enter old password"
                      />
                      {touched.oldPassword && errors.oldPassword && (
                        <FormHelperText error id="helper-text-old-password-reset">
                          {errors.oldPassword}
                        </FormHelperText>
                      )}
                    </Stack>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="password-reset">Password</InputLabel>
                    <OutlinedInput
                      fullWidth
                      error={Boolean(touched.password && errors.password)}
                      id="password-reset"
                      type={showPassword ? 'text' : 'password'}
                      value={values.password}
                      name="password"
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        changePassword(e.target.value);
                      }}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            color="secondary"
                          >
                            {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Enter password"
                    />
                    {touched.password && errors.password && (
                      <FormHelperText error id="helper-text-password-reset">
                        {errors.password}
                      </FormHelperText>
                    )}
                  </Stack>
                  {renderPasswordStrengthIndicator()}
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="confirm-password-reset">Confirm Password</InputLabel>
                    <OutlinedInput
                      fullWidth
                      error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                      id="confirm-password-reset"
                      type="password"
                      value={values.confirmPassword}
                      name="confirmPassword"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Enter confirm password"
                    />
                    {touched.confirmPassword && errors.confirmPassword && (
                      <FormHelperText error id="helper-text-confirm-password-reset">
                        {errors.confirmPassword}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>

                {errors.submit && (
                  <Grid item xs={12}>
                    <FormHelperText error>
                      {Array.isArray(errors.submit) ? errors.submit.map((msg, index) => <div key={index}>{msg}</div>) : errors.submit}
                    </FormHelperText>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Stack alignItems={'center'} sx={{ pt: 1.5 }}>
                    <AnimateButton disabled={disabledButton}>
                      <StyledButton
                        disableElevation
                        disabled={disabledButton}
                        size="large"
                        type="submit"
                        variant="contained"
                        color="primary"
                      >
                        Change Password
                      </StyledButton>
                    </AnimateButton>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

export default AuthChangePassword;
